import type { DateRange, DateValue } from 'reka-ui';

// Single-use reactive logic, kept out of index.vue's <script setup> for size — same co-location
// pattern as this folder's state.ts, which is single-use for the same reason.
interface DesktopCalendarPlacementOptions {
  model: Ref<DateRange | undefined>;
  isOpen: Ref<boolean>;
  isDesktop: Ref<boolean>;
  mode: Ref<'start' | 'end'>;
  startSelectionInProgress: Ref<boolean>;
  startCalendarReference: Ref<HTMLElement | null>;
  endCalendarReference: Ref<HTMLElement | null>;
  firstSelectableDate: Ref<DateValue>;
}

export const useDesktopCalendarPlacement = (options: DesktopCalendarPlacementOptions) => {
  const desktopPopoverSide = useDesktopPopoverSide(options);
  const { startDesktopPlaceholder, onStartDesktopPlaceholderUpdate } = useStartDesktopPlaceholder(options);

  return { desktopPopoverSide, startDesktopPlaceholder, onStartDesktopPlaceholderUpdate };
};

// Reset only when a fresh open starts, so the close animation keeps the locked side and doesn't flicker.
const useDesktopPopoverSide = (options: DesktopCalendarPlacementOptions) => {
  const { isOpen, isDesktop, mode, startCalendarReference, endCalendarReference } = options;
  const desktopPopoverSide = ref<'top' | 'bottom' | 'left' | 'right'>();

  watch(isOpen, async (open) => {
    if (!open || !isDesktop.value) {
      return;
    }
    desktopPopoverSide.value = undefined;
    await nextTick();
    const calendarElement = mode.value === 'start' ? startCalendarReference.value : endCalendarReference.value;
    const side = calendarElement?.closest<HTMLElement>('[data-side]')?.dataset.side;
    if (side === 'top' || side === 'bottom' || side === 'left' || side === 'right') desktopPopoverSide.value = side;
  });

  return desktopPopoverSide;
};

// reka-ui recentres the grid on its own range anchor whenever that anchor moves, which fights the month
// the user navigated to. Two anchor moves are ours, not navigation, so their placeholder update is dropped:
// picking a departure date, and hovering a cell while a return date exists — the hover swaps the anchor to
// that return date (see models.ts), which would otherwise snap the grid back to the already-selected month.
const useStartDesktopPlaceholder = (options: DesktopCalendarPlacementOptions) => {
  const { model, isOpen, isDesktop, startSelectionInProgress, firstSelectableDate } = options;
  const startDesktopPlaceholder = shallowRef<DateValue>(firstSelectableDate.value);
  const ignoreNextUpdate = useIgnoreNextDateClick(options.startCalendarReference);

  // Open on the first selectable month, not today's: a later bound (the previous flight's date) or a start date left
  // under it would otherwise open on months where no day can be picked.
  watch(isOpen, (open) => {
    if (!open || !isDesktop.value) {
      return;
    }
    const start = model.value?.start;
    startDesktopPlaceholder.value =
      start && start.compare(firstSelectableDate.value) >= 0 ? start : firstSelectableDate.value;
  });

  watch(startSelectionInProgress, () => {
    armUntilNextTick(ignoreNextUpdate);
  });

  const onStartDesktopPlaceholderUpdate = (value: DateValue | undefined): void => {
    if (ignoreNextUpdate.value) {
      ignoreNextUpdate.value = false;
      return;
    }
    if (!value) {
      return;
    }
    startDesktopPlaceholder.value = value;
  };

  return { startDesktopPlaceholder, onStartDesktopPlaceholderUpdate };
};

// Lowered again straight away because reka-ui stays silent when the anchor lands on the day already on
// screen: a guard left up would swallow the user's next month navigation.
const armUntilNextTick = (guard: Ref<boolean>): void => {
  guard.value = true;
  void nextTick(() => {
    guard.value = false;
  });
};

// Arms a flag on the capture phase of a date-cell click, ahead of reka-ui's own bubble-phase handler.
const useIgnoreNextDateClick = (calendarReference: Ref<HTMLElement | null>) => {
  const ignoreNextUpdate = ref(false);

  useEventListener(
    calendarReference,
    'click',
    (event) => {
      if (event.target instanceof Element && event.target.closest('[data-date]')) {
        ignoreNextUpdate.value = true;
      }
    },
    { capture: true },
  );

  return ignoreNextUpdate;
};
